import { createContext, PropsWithChildren, useEffect } from "react";
import { initPush } from "../../lib/push";
import { syncExistingWebPushSubscription } from "../../lib/webpush";
import { hydrateServerNotificationPreferences, readNotificationPreferences, registerPushIfNeeded } from "../../lib/notifications";
import { useAuth } from "../auth/AuthProvider";

type PushContextValue = { ready: boolean };

const PushContext = createContext<PushContextValue | undefined>(undefined);

export function PushProvider({ children }: PropsWithChildren) {
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    let canceled = false;
    (async () => {
      const prefs = await hydrateServerNotificationPreferences(token);
      if (canceled) return;
      if (prefs.mobilePushEnabled) initPush(token);
      if (prefs.webPushEnabled) syncExistingWebPushSubscription(token);
    })();
    return () => { canceled = true; };
  }, [token]);

  useEffect(() => {
    if (token && shouldEnableWebPush()) syncExistingWebPushSubscription(token).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const registerIfAllowed = (force = false) => {
      if (!shouldEnableMobilePush() && !shouldEnableWebPush()) return;
      registerPushIfNeeded(token, {
        initPush: async (t) => { if (shouldEnableMobilePush()) await initPush(t); },
        registerWebPush: async (t) => shouldEnableWebPush() ? syncExistingWebPushSubscription(t) : false,
      }, force);
    };
    registerIfAllowed(true);
    const onFocus = () => { registerIfAllowed(false); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [token]);

  return <PushContext.Provider value={{ ready: true }}>{children}</PushContext.Provider>;
}

function shouldEnableWebPush(): boolean {
  return readNotificationPreferences().webPushEnabled;
}

function shouldEnableMobilePush(): boolean {
  return readNotificationPreferences().mobilePushEnabled;
}
